import { memo, useRef } from "react";
import Grid from "@mui/material/Grid";
import QuantityInput from "../../common/QuantityInput";
import {
  AutocompleteInput,
  maxLength,
  minLength,
  ReferenceInput,
  required,
} from "react-admin";
import CodeDetailInput from "../../common/CodeDetailInput";
import {
  inputTextProduct,
  matchSuggestionProduct,
  OptionRendererProduct,
} from "../../../common/OptionRendererProduct";
import ProductInput from "../../../purchaseOrders/common/ProductInput";
import { useFormContext } from "react-hook-form";
import useEnterToAddRowAndFocus from "../../../../utils/useEnterToAddRowAndFocus";

const uniqueItem = (value, allValues) => {
  if (!allValues.mutation_inventory_write_off_items) return undefined;
  let count = 0;
  for (
    let i = 0;
    i < allValues.mutation_inventory_write_off_items.length;
    i++
  ) {
    const elm = allValues.mutation_inventory_write_off_items[i];
    if (value === elm.product_id) {
      if (count > 0) return "Duplicate is not allowed";
      count += 1;
    }
  }
  return undefined;
};

const validateCode = [required(), minLength(36), maxLength(36), uniqueItem];
const validateLocation = [required(), minLength(36), maxLength(36)];

const CstInput = ({
  quantity,
  formData,
  idSource,
  quantitySource,
  nameSource,
  qualitySource,
  locationSource,
}) => {
  const { setValue } = useFormContext();

  // 🔹 Ref ke Quantity
  const quantityRef = useRef(null);

  // 🔹 Enter ➜ tambah baris baru + fokus ke Product berikutnya
  useEnterToAddRowAndFocus(quantityRef, undefined, 'input[name*="product_id"]');

  const onCodeChangeHandler = async (code) => {
    if (code.length !== 36) {
      setValue(quantitySource, "");
      setValue(locationSource, "");
      setValue(nameSource, "");
      setValue(qualitySource, "");
    }
  };

  // 🔹 Kecualikan produk yang sudah dipilih
  const exclude = {};
  const exclude_ids = [];
  const items = formData.mutation_inventory_write_off_items || [];
  for (let i = 0; i < items.length; i++) {
    if (items[i].product_id) {
      exclude_ids.push(
        items[i].product_id
      );
    }
  }
  if (exclude_ids.length) {
    exclude["products.exclude_ids"] = exclude_ids;
  }

  return (
    <Grid
      container
      spacing={2}
      sx={{
        "& .MuiGrid-item": { py: 0 },
        mt: 0,
      }}
    >
      {/* 🔹 Product */}
      <Grid item xs={12} lg={3}>
        <ProductInput
          fullWidth
          source={idSource}
          variant="outlined"
          validate={validateCode}
          label="mine.label.product_code"
          onChange={onCodeChangeHandler}
          dependOn="date"
          reset={[
            quantitySource,
            locationSource,
            nameSource,
            qualitySource,
          ]}
          detailType="noZeroRemain"
          defaultFilters={{ "products.active": 1 }}
          exclude={exclude}
          sx={{ display: "inline-flex" }}
          optionText={<OptionRendererProduct />}
          inputText={inputTextProduct}
          matchSuggestion={matchSuggestionProduct}
          helperText={false}
        />
      </Grid>

      {/* 🔹 Warehouse */}
      <Grid item xs={12} lg={1.4}>
        <ReferenceInput
          source={locationSource}
          reference="warehouses"
          perPage={10}
          sort={{ field: "warehouses.created_at", order: "ASC" }}
        >
          <AutocompleteInput
            variant="outlined"
            label="mine.label.warehouse"
            fullWidth
            validate={validateLocation}
            optionText="name"
            helperText={false}
            sx={{ "&>.RaAutocompleteInput-textField": { minWidth: "auto" } }}
          />
        </ReferenceInput>
      </Grid>

      {/* 🔹 Quantity (pindah ke kanan warehouse) */}
      <Grid item xs={12} lg={1.2} ref={quantityRef}>
        <QuantityInput
          variant="outlined"
          source={quantitySource}
          label="mine.label.quantity"
          fullWidth
          quantity={quantity}
          formData={formData}
          productSource={idSource}
          helperText={false}
        />
      </Grid>

      {/* 🔹 Detail produk */}
      <CodeDetailInput
        nameSource={nameSource}
        qualitySource={qualitySource}
        dependOn={idSource}
      />
    </Grid>
  );
};

// 🔹 Cegah re-render berlebihan
const propsAreEqual = (prevProps, nextProps) => {
  return (
    prevProps.formData === nextProps.formData &&
    prevProps.idSource === nextProps.idSource &&
    prevProps.quantitySource === nextProps.quantitySource
  );
};

const MemoizedInput = memo(CstInput, propsAreEqual);

const Inputs = ({ formData, scopedFormData, getSource }) => {
  return (
    <MemoizedInput
      {...scopedFormData}
      formData={formData}
      idSource={getSource("product_id")}
      quantitySource={getSource("quantity")}
      nameSource={getSource("name")}
      qualitySource={getSource("quality")}
      locationSource={getSource("warehouse_id")}
    />
  );
};

export default Inputs;
